import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_BASE_URL } from '@/utils/constants';
import { queryClient, ORG_SCOPE } from '@/shared/services/query-client';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  is_active: boolean;
}

export interface AuthOrg {
  id: string;
  name: string;
  slug: string;
}

export interface AuthMembership {
  id: string;
  org: AuthOrg;
  role: 'owner' | 'admin' | 'analyst' | 'viewer';
}

interface TokenPair {
  access_token: string;
  refresh_token: string;
}

interface MeResponse {
  user: AuthUser;
  memberships: AuthMembership[];
}

interface AuthState {
  /** Short-lived access token. In memory only — refresh-token in HttpOnly cookie. */
  accessToken: string | null;
  /** Legacy slot retained for the api-client interface; always null in cookie mode. */
  refreshToken: string | null;
  user: AuthUser | null;
  memberships: AuthMembership[];
  currentOrgId: string | null;
  isLoadingMe: boolean;
  /** True until we've attempted silent refresh on first load. */
  isBootstrapping: boolean;

  signup: (email: string, password: string, displayName?: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setTokens: (access: string, refresh: string | null) => void;
  loadMe: () => Promise<void>;
  selectOrg: (orgId: string) => void;
  bootstrap: () => Promise<void>;
}

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  const data = await r.json().catch(() => null);
  if (!r.ok) {
    const msg = data?.error?.message ?? r.statusText;
    throw new Error(msg);
  }
  return data as T;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      memberships: [],
      currentOrgId: null,
      isLoadingMe: false,
      isBootstrapping: true,

      signup: async (email, password, displayName) => {
        const tokens = await postJSON<TokenPair>('/auth/signup', {
          email,
          password,
          display_name: displayName || undefined,
        });
        set({ accessToken: tokens.access_token, refreshToken: null });
        await get().loadMe();
      },

      login: async (email, password) => {
        const tokens = await postJSON<TokenPair>('/auth/login', { email, password });
        set({ accessToken: tokens.access_token, refreshToken: null });
        await get().loadMe();
      },

      logout: async () => {
        const { accessToken } = get();
        if (accessToken) {
          await fetch(`${API_BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          }).catch(() => undefined);
        }
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          memberships: [],
          currentOrgId: null,
        });
        // Drop every cached org-scoped query so nothing from the prior session leaks.
        queryClient.removeQueries({ queryKey: [ORG_SCOPE] });
      },

      setTokens: (access, _refresh) =>
        // Body refresh_token is ignored in cookie mode — kept for api-client compat.
        set({ accessToken: access, refreshToken: null }),

      loadMe: async () => {
        const { accessToken } = get();
        if (!accessToken) return;
        set({ isLoadingMe: true });
        try {
          const r = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          });
          if (!r.ok) {
            await get().logout();
            return;
          }
          const me = (await r.json()) as MeResponse;
          set((s) => ({
            user: me.user,
            memberships: me.memberships,
            currentOrgId:
              s.currentOrgId && me.memberships.some((m) => m.org.id === s.currentOrgId)
                ? s.currentOrgId
                : me.memberships[0]?.org.id ?? null,
          }));
        } finally {
          set({ isLoadingMe: false });
        }
      },

      selectOrg: (orgId) => {
        const m = get().memberships.find((mem) => mem.org.id === orgId);
        if (m) {
          set({ currentOrgId: orgId });
          // Org switch — invalidate everything keyed by org so the new tenant
          // doesn't accidentally render the previous tenant's data.
          queryClient.removeQueries({ queryKey: [ORG_SCOPE] });
        }
      },

      bootstrap: async () => {
        // On app load: try silent refresh via cookie. If it succeeds we have a
        // fresh access token; if not, the user lands on /login.
        try {
          const r = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
            credentials: 'include',
          });
          if (r.ok) {
            const data = (await r.json()) as TokenPair;
            set({ accessToken: data.access_token, refreshToken: null });
            await get().loadMe();
          }
        } catch {
          // Network error during bootstrap — leave state empty.
        } finally {
          set({ isBootstrapping: false });
        }
      },
    }),
    {
      name: 'auth-state',
      // Only persist the chosen org — access token must stay in memory,
      // refresh token lives in the HttpOnly cookie.
      partialize: (s) => ({ currentOrgId: s.currentOrgId }),
    },
  ),
);

export const selectIsAuthenticated = (s: AuthState): boolean => Boolean(s.accessToken);
